import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { bulletinsAPI, errMsg } from '../../lib/regulatoryAPI';

const SOURCES = ['ATO', 'TPB', 'IPA', 'CPA', 'CA ANZ', 'Other'];

export const BulletinSettings = ({ onSaved }) => {
  const [s, setS] = useState(null);
  const [topics, setTopics] = useState('');
  const [busy, setBusy] = useState(false);
  const apply = (d) => { setS(d); setTopics((d.topics || []).join('\n')); };
  useEffect(() => { bulletinsAPI.settings().then(apply).catch((e) => toast.error(errMsg(e))); }, []);
  if (!s) return null;
  const setFeed = (i, k, v) => setS({ ...s, feeds: s.feeds.map((f, j) => (j === i ? { ...f, [k]: v } : f)) });
  const save = async () => {
    try {
      apply(await bulletinsAPI.saveSettings({ feeds: s.feeds.map(({ url, source, enabled }) => ({ url, source, enabled })), topics: topics.split('\n') }));
      toast.success('Settings saved'); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const fetchNow = async () => {
    setBusy(true);
    try { const r = await bulletinsAPI.fetchNow(); setS({ ...s, feeds: r.feeds }); toast.success(`Imported ${r.imported} new item(s)`); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="grid gap-6 pt-2 lg:grid-cols-2">
      <Card><CardHeader><CardTitle className="text-base">RSS feeds (auto-import every 6 hours)</CardTitle></CardHeader>
        <CardContent className="space-y-3" data-testid="bulletin-feeds">
          {!s.feeds.length && <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground" data-testid="bulletin-no-feed-note">{s.no_feed_note}</p>}
          {s.feeds.map((f, i) => (
            <div key={i} className="space-y-1.5 rounded-md border p-3" data-testid={`bulletin-feed-${i}`}>
              <div className="flex gap-2">
                <Input value={f.url} onChange={(e) => setFeed(i, 'url', e.target.value)} placeholder="https://…/feed.xml" data-testid={`bulletin-feed-url-${i}`} />
                <select className="h-9 rounded-md border bg-background px-2 text-sm" value={f.source} onChange={(e) => setFeed(i, 'source', e.target.value)} data-testid={`bulletin-feed-source-${i}`}>{SOURCES.map((x) => <option key={x}>{x}</option>)}</select>
                <Switch checked={f.enabled} onCheckedChange={(v) => setFeed(i, 'enabled', v)} data-testid={`bulletin-feed-enabled-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => setS({ ...s, feeds: s.feeds.filter((_, j) => j !== i) })} data-testid={`bulletin-feed-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <p className="text-xs text-muted-foreground">Last fetched: {f.last_fetched_at?.slice(0, 16).replace('T', ' ') || 'never'} · imported {f.last_imported || 0}</p>
              {f.last_error && <p className="text-xs text-red-600" data-testid={`bulletin-feed-error-${i}`}>Last error: {f.last_error}</p>}
            </div>))}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setS({ ...s, feeds: [...s.feeds, { url: '', source: 'ATO', enabled: true }] })} data-testid="bulletin-feed-add"><Plus className="mr-2 h-4 w-4" />Add feed</Button>
            <Button variant="outline" onClick={fetchNow} disabled={busy || !s.feeds.length} data-testid="bulletin-fetch-now"><RefreshCw className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} />Fetch now</Button>
          </div>
        </CardContent></Card>
      <Card><CardHeader><CardTitle className="text-base">Topics (one per line)</CardTitle></CardHeader>
        <CardContent><Textarea rows={12} value={topics} onChange={(e) => setTopics(e.target.value)} data-testid="bulletin-topics-input" /></CardContent></Card>
      <div className="lg:col-span-2"><Button onClick={save} data-testid="bulletin-settings-save">Save settings</Button></div>
    </div>
  );
};
